// Leergutkonten: je Kunde und je Lieferant der Saldo in Stück und Geld aus dem Leergutjournal des Kerns, der Auszug
// mit jeder Bewegung und ihrem Beleg – und die Korrektur als Beleg mit Grund (nie eine stille Änderung).
//
// Vorzeichen: + = der Partner hält Leergut. Beim Kunden heißt das „er schuldet uns Leergut bzw. Pfand“, beim
// Lieferanten „wir halten sein Leergut und bekommen bei Rückgabe das Pfand zurück“.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { defineDocumentContracts, zDocumentView } from '@shared/document'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { DEPOSIT_ACCOUNTS, zDay, zPieceQty, zReason } from './common'

const READ = 'deposits.account.read'
const ADJUST = 'deposits.account.adjust'

export const ADJUSTMENT_TYPE = 'deposit_adjustment'
export const SUPPLIER_ADJUSTMENT_TYPE = 'deposit_adjustment_supplier'

const zAccountRow = z.object({
  partnerId: zId,
  number: z.string(),
  name: z.string(),
  account: z.enum(DEPOSIT_ACCOUNTS),
  /** Pfandwert außer Haus in Cent (Summe aller Pfandarten) */
  amountCents: z.int(),
  /** Gebinde außer Haus: Leerkästen, Fässer und andere Rahmen (Stück in Tausendstel, ohne Einzelflaschen) */
  containerQty: z.int(),
  /** alle Stücke einschließlich Flaschen */
  pieceQty: z.int(),
  lastMovementOn: z.string().nullable(),
})

export const accountList = defineContract({
  name: 'deposits.account.list',
  title: 'Leergutkonten anzeigen',
  description: 'Kunden und Lieferanten mit Leergut außer Haus: Stück und Pfandwert je Konto.',
  llm: {
    description:
      'Listet die Leergutkonten: je Kunde bzw. Lieferant den Pfandwert außer Haus in Cent und die Stückzahl. ' +
      '`account` customer = Leergut bei Kunden, supplier = Leergut von Lieferanten bei uns. Ohne `includeZero` nur ' +
      'Konten mit Saldo. Den Auszug eines Partners liefert deposits.account.statement.',
    keywords: ['Leergutkonto', 'Leergutkonten', 'Pfand außer Haus', 'Leergut beim Kunden', 'Pfandsaldo'],
  },
  input: listInput({
    sortBy: ['amountCents', 'name', 'lastMovementOn'],
    defaultSort: { by: 'amountCents', dir: 'desc' },
    filters: { account: z.enum(DEPOSIT_ACCOUNTS).optional(), includeZero: z.boolean().optional() },
  }),
  output: listOutput(zAccountRow).extend({ totals: z.object({ customerCents: z.int(), supplierCents: z.int() }) }),
  permission: READ,
  risk: 'read',
})

export const accountStatement = defineContract({
  name: 'deposits.account.statement',
  title: 'Leergutkonto-Auszug',
  description: 'Saldo je Pfandart und alle Bewegungen eines Leergutkontos mit Beleg – zum Ansehen und Drucken.',
  llm: {
    description:
      'Liefert den Auszug des Leergutkontos EINES Partners (Kunde oder Lieferant): Saldo je einfacher Pfandart in ' +
      'Stück (Tausendstel) und Cent sowie die Bewegungen mit Datum, Pfandart, Stück, Betrag, Belegnummer und Belegart ' +
      '(jüngste zuerst). + = der Partner hat Leergut erhalten, − = zurückgegeben. Ohne `account` beide Seiten.',
    keywords: ['Leergutauszug', 'Kontoauszug Leergut', 'Leergutkonto', 'wie viel Leergut hat', 'Pfand offen'],
  },
  input: z.strictObject({ partnerId: zId, account: z.enum(DEPOSIT_ACCOUNTS).optional(), depositTypeId: zId.optional(), limit: z.int().min(1).max(500).default(200) }),
  output: z.object({
    partner: z.object({ id: zId, number: z.string(), name: z.string(), isCustomer: z.boolean(), isSupplier: z.boolean() }),
    /** Stichtag des Auszugs (heute) */
    date: z.string(),
    sides: z.array(
      z.object({
        account: z.enum(DEPOSIT_ACCOUNTS),
        amountCents: z.int(),
        balances: z.array(z.object({ depositTypeId: zId, name: z.string(), shortName: z.string().nullable(), qty: z.int(), amountCents: z.int() })),
      }),
    ),
    movements: z.array(
      z.object({
        id: zId,
        account: z.enum(DEPOSIT_ACCOUNTS),
        businessDate: z.string(),
        postedAt: z.date(),
        depositTypeId: zId,
        name: z.string(),
        qty: z.int(),
        amountCents: z.int(),
        isReversal: z.boolean(),
        note: z.string().nullable(),
        document: z.object({ id: zId, type: z.string(), typeTitle: z.string(), number: z.string().nullable() }).nullable(),
      }),
    ),
    /** es gibt ältere Bewegungen als die gelieferten */
    truncated: z.boolean(),
    canAdjust: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
})

const PERMISSIONS = { read: READ, write: ADJUST, post: ADJUST, reverse: ADJUST } as const

/** Korrektur eines Kunden-Leergutkontos (Belegart 'deposit_adjustment') */
export const adjustment = defineDocumentContracts({
  module: 'deposits',
  entity: 'adjustment',
  documentType: ADJUSTMENT_TYPE,
  title: 'Leergutkonto-Korrektur',
  titlePlural: 'Leergutkonto-Korrekturen',
  permissions: PERMISSIONS,
  financial: true,
  keywords: ['Leergutkonto korrigieren', 'Leergut ausbuchen', 'Pfand korrigieren', 'Leergutdifferenz'],
})

/** Korrektur eines Lieferanten-Leergutkontos (Belegart 'deposit_adjustment_supplier') */
export const supplierAdjustment = defineDocumentContracts({
  module: 'deposits',
  entity: 'supplierAdjustment',
  documentType: SUPPLIER_ADJUSTMENT_TYPE,
  title: 'Leergutkonto-Korrektur (Lieferant)',
  titlePlural: 'Leergutkonto-Korrekturen (Lieferanten)',
  permissions: PERMISSIONS,
  financial: true,
  keywords: ['Lieferanten-Leergutkonto korrigieren', 'Leergutdifferenz Lieferant'],
})

export const adjustmentGet = adjustment.get
export const adjustmentList = adjustment.list
export const adjustmentSaveDraft = adjustment.saveDraft
export const adjustmentPost = adjustment.post
export const adjustmentReverse = adjustment.reverse
export const adjustmentTransition = adjustment.transition
export const adjustmentPrint = adjustment.print
export const adjustmentDiscard = adjustment.discard
export const supplierAdjustmentGet = supplierAdjustment.get
export const supplierAdjustmentList = supplierAdjustment.list
export const supplierAdjustmentSaveDraft = supplierAdjustment.saveDraft
export const supplierAdjustmentPost = supplierAdjustment.post
export const supplierAdjustmentReverse = supplierAdjustment.reverse
export const supplierAdjustmentTransition = supplierAdjustment.transition
export const supplierAdjustmentPrint = supplierAdjustment.print
export const supplierAdjustmentDiscard = supplierAdjustment.discard

/** increase = der Partner hält MEHR Leergut als gebucht (Konto +) · decrease = weniger (Konto −) */
export const ADJUST_DIRECTIONS = ['increase', 'decrease'] as const

export const accountAdjust = defineContract({
  name: 'deposits.account.adjust',
  title: 'Leergutkonto korrigieren',
  description: 'Korrigiert ein Leergutkonto mit Grund – als Beleg, der im Auszug steht und sich stornieren lässt.',
  llm: {
    description:
      'Bucht eine Korrektur auf das Leergutkonto eines Kunden (`account: customer`) oder Lieferanten (`supplier`): je ' +
      'Zeile Pfandart, Stück in Tausendstel und Richtung – `increase` = der Partner hält mehr Leergut als gebucht ' +
      '(Konto +), `decrease` = weniger (Konto −). Ein Grund ist Pflicht. Es entsteht ein Beleg; der Leergutbestand am ' +
      'Standort ändert sich NICHT. Nicht für echte Rückgaben: Leergut vom Kunden deposits.customerReturn.book, an den ' +
      'Lieferanten deposits.supplierReturn.book.',
    keywords: ['Leergutkonto korrigieren', 'Leergut ausbuchen', 'Leergutdifferenz', 'Pfandkonto bereinigen', 'Kästen abschreiben'],
  },
  input: z.strictObject({
    partnerId: zId,
    account: z.enum(DEPOSIT_ACCOUNTS).default('customer'),
    /** Standort, der die Korrektur verantwortet (Belegkopf) */
    locationId: zId,
    reason: zReason,
    documentDate: zDay.optional(),
    lines: z
      .array(z.strictObject({ depositTypeId: zId, direction: z.enum(ADJUST_DIRECTIONS), qty: zPieceQty.describe('Stück der Pfandart in Tausendstel (2 Kästen = 2000)') }))
      .min(1, { error: 'Bitte mindestens eine Pfandart angeben.' })
      .max(50),
  }),
  output: z.object({ document: zDocumentView, message: z.string() }),
  permission: ADJUST,
  risk: 'financial',
  fieldPermissions: { 'document.lines.*.costCents': PERMISSION_COSTS_READ },
  effects: { reversible: true },
})
